import { Fragment, useState, useEffect } from "react";
import { Disclosure, Menu, Transition } from "@headlessui/react";
import {
  ChevronDownIcon,
  CheckIcon,
  XIcon,
  DownloadIcon,
  TicketIcon,
  QrcodeIcon,
  ShoppingBagIcon,
  VideoCameraIcon,
  CalendarIcon,
  ThumbUpIcon,
  CreditCardIcon,
  UserCircleIcon,
  CollectionIcon,
  SparklesIcon,
  UsersIcon,
  MenuIcon,
} from "@heroicons/react/outline";
import Link from "next/link";
import { ILoggedUser } from "pages";
import { INearestSession } from "types/sessions";
import useExpertSessionPolling from "hooks/useExpertSessionPolling";
import useClientSessionPolling from "hooks/useClientSessionPolling";
// import { getFullName } from "utils";
import { LANGUAGES } from "hooks/useLanguage";
import Banner from "components/Banner/Banner";
import { useUser } from "hooks/useUser";
import { useRouter } from "next/router";
import CookieBanner from "components/CookieBanner/CookieBanner";
import SupportModal from "components/SupportModal/SupportModal";
import { Link as ScrollLink } from "react-scroll";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { api } from "utils/api";
import Cookies from "js-cookie";
import { normalizeLanguageCode } from "utils";

export default function PublicNav({
  loggedUser,
  imagesLoaded,
  language,
}: {
  loggedUser: ILoggedUser;
  imagesLoaded: boolean;
  language?: string;
}) {
  const router = useRouter();
  const expertSession = useExpertSessionPolling();
  const clientSession = useClientSessionPolling();

  const [isMainOpen, setIsMainOpen] = useState(true);
  const [isProductOpen, setIsProductOpen] = useState(false);
  const [isMobileLanguageMenuOpen, setIsMobileLanguageMenuOpen] =
    useState(false);
  const [isMenuOpen, setMenuOpen] = useState(true);
  const [isSupportModalOpen, setisSupportModalOpen] = useState(false);
  const { i18n } = useTranslation();
  const { t } = useTranslation();

  const mutation = useMutation((payload: { language: string }) =>
    api.patch("/users/v1/users", payload)
  );

  const changeLanguageNext = (newLanguage) => {
    const normalizedLanguage = normalizeLanguageCode(newLanguage);

    i18n.changeLanguage(normalizedLanguage);
    mutation.mutate({ language: normalizedLanguage });
    Cookies.set("language", normalizedLanguage);
  };

  useEffect(() => {
    if (language) {
      changeLanguageNext(language);
    }
  }, [language]);

  const handleProductClick = () => {
    setIsMainOpen(!isMainOpen);
    setIsProductOpen(!isProductOpen);
  };

  const handleSupportClick = () => {
    setMenuOpen(false);
    setisSupportModalOpen(true);
  };

  const appUrl = loggedUser?.isExpert ? "/my/dashboard" : "/my/purchased";

  const { signOut } = useUser();

  const handleSignOut = async () => {
    Cookies.remove("language");
    Cookies.remove("userRole");
    await signOut();
    router.reload();
  };

  return (
    <>
      {expertSession.activeSessions?.length > 0 &&
        expertSession.activeSessions.map((session: INearestSession) => {
          const sessionPath = `/my/appointment/${session.id}`;

          if (window.location.pathname === sessionPath) {
            return null;
          }

          return (
            <Banner
              key={session.id}
              onClick={() => (window.location.href = sessionPath)}
              avatar={session.expert.avatar}
              userAvatar={session?.user?.avatar}
              communication={session.communication}
            />
          );
        })}

      {clientSession.activeSessions?.length > 0 &&
        clientSession.activeSessions.map((session: INearestSession) => {
          const sessionPath = `/my/appointment/${session.id}`;

          if (window.location.pathname === sessionPath) {
            return null;
          }

          return (
            <Banner
              key={session.id}
              onClick={() => (window.location.href = sessionPath)}
              avatar={session.expert.avatar}
              userAvatar={session?.user?.avatar}
              communication={session.communication}
            />
          );
        })}

      {expertSession.activeSingleSessions.length > 0
        ? expertSession.activeSingleSessions.map(
            (session: { id: string; date: string; expert: any; user: any }) => (
              <Banner
                key={session.id}
                backgroundColor="bg-pink-600"
                iconColor="bg-pink-700"
                communication="call"
                onClick={() => window.location.assign(`/session/${session.id}`)}
                avatar={session.expert.avatar}
                userAvatar={session?.user?.avatar}
              />
            )
          )
        : null}
      <CookieBanner />
      <Disclosure as="nav" className="w-full relative top-0 z-30">
        {() => (
          <>
            <div className="px-4 pt-4">
              <div
                className={`mx-auto max-w-2xl overflow-hidden md:overflow-visible border border-white/10 bg-gradient-to-r from-[#0f0505] via-[#1a0a0a] to-[#0f0505] shadow-[0_4px_20px_-1px_rgba(0,0,0,0.64)] backdrop-blur ${
                  isMobileLanguageMenuOpen ? "rounded-[28px]" : "rounded-full"
                }`}
              >
                <div
                  className={`flex items-center justify-between ${
                    isMobileLanguageMenuOpen ? "px-5 pt-3 pb-2" : "px-5 py-1.5"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Link href="/" className="flex items-center gap-2">
                      <img
                        width={101}
                        height={29}
                        src="/img/logo/waylight-standart-logo.svg"
                        alt="Waylight logo"
                        className="h-7"
                      />
                    </Link>
                    <span className="hidden md:block h-6 w-px bg-white/10" />
                    <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-white">
                      <ScrollLink
                        to="features"
                        smooth={true}
                        duration={500}
                        offset={!imagesLoaded ? 0 : 0}
                        className="cursor-pointer transition text-white hover:text-white"
                      >
                        {t("landingNavProduct")}
                      </ScrollLink>
                      <a
                        href="#pricing"
                        className="cursor-pointer transition text-white hover:text-white"
                      >
                        {t("landingNavPricing")}
                      </a>
                      <a
                        href="#reviews"
                        className="cursor-pointer transition text-white hover:text-white"
                      >
                        {t("landingNavReviews")}
                      </a>
                    </nav>
                  </div>

                  <div className="flex items-center gap-2">
                    <a className="py-1" href={appUrl}>
                      <button className="flex items-center gap-2 text-white max-w-[160px]">
                        <div className="h-8 w-8 md:h-9 md:w-9 rounded-full border border-white/10 bg-white/10">
                          <img
                            alt="image avatar"
                            src={
                              loggedUser?.isExpert
                                ? loggedUser?.expertAvatar
                                : loggedUser?.avatar
                            }
                            className="h-8 w-8 md:h-9 md:w-9 rounded-full bg-avatar-gradient"
                          />
                        </div>
                      </button>
                    </a>

                    <span className="hidden md:block h-6 w-px bg-white/10" />

                    <Menu as="div" className="relative z-10 hidden md:block">
                      {({ open }) => (
                        <>
                          <Menu.Button className="flex items-center gap-2 py-1 text-white focus:outline-none focus:ring-2 focus:ring-black">
                            {i18n.language.toUpperCase()}
                            <ChevronDownIcon className="w-4 h-4" />
                          </Menu.Button>
                          <Transition
                            show={open}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <Menu.Items
                              static
                              className="absolute -left-[390%] top-[2vh] flex h-[479px] w-[343px] flex-col rounded-2xl bg-gradient-to-b from-[#140707] via-[#140707] to-[#0f0505] p-5 text-white shadow-[0_24px_60px_rgba(0,0,0,0.45)] ring-1 ring-white/10 focus:outline-none"
                            >
                              <p className="text-sm text-white">
                                {t("changeLanguage")}
                              </p>
                              <div className="mt-4 min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
                                {LANGUAGES.map((lang) => {
                                  const isActive =
                                    i18n.language.toLowerCase() ===
                                    lang.code.toLowerCase();
                                  return (
                                    <Menu.Item key={lang.id}>
                                      <button
                                        className="flex w-full items-center justify-between rounded-2xl px-2 py-1.5 text-left text-sm text-white/90 transition hover:bg-white/5"
                                        onClick={() =>
                                          changeLanguageNext(
                                            lang.code.toLowerCase()
                                          )
                                        }
                                      >
                                        <span>{lang.name}</span>
                                        {isActive && (
                                          <CheckIcon className="h-5 w-5 text-emerald-400" />
                                        )}
                                      </button>
                                    </Menu.Item>
                                  );
                                })}
                              </div>
                              <div className="mt-4 border-t border-white/10 pt-4">
                                <Menu.Item>
                                  <button className="w-full rounded-full border border-white/10 bg-white/[0.08] py-2 text-sm font-semibold text-white shadow-[inset_0_0_8px_1px_rgba(255,255,255,0.16)] transition hover:bg-white/[0.12]">
                                    {t("close")}
                                  </button>
                                </Menu.Item>
                              </div>
                            </Menu.Items>
                          </Transition>
                        </>
                      )}
                    </Menu>

                    <button
                      type="button"
                      className="inline-flex items-center rounded-full border border-white/20 bg-gradient-to-b from-white/15 to-white/5 px-5 py-2 text-sm font-semibold text-white shadow-[inset_0_0_8px_1px_rgba(255,255,255,0.16)] backdrop-blur-[2px] transition hover:from-white/20 hover:to-white/10"
                      onClick={handleSignOut}
                    >
                      {t("signOut")}
                    </button>

                    <button
                      type="button"
                      className="flex h-10 w-10 items-center justify-center md:hidden"
                      aria-label={t("landingNavOpenMenu")}
                      aria-expanded={isMobileLanguageMenuOpen}
                      onClick={() =>
                        setIsMobileLanguageMenuOpen((previous) => !previous)
                      }
                    >
                      <MenuIcon className="w-6 h-6 text-white" />
                    </button>
                  </div>
                </div>
                {isMobileLanguageMenuOpen && (
                  <div className="md:hidden">
                    <div className="px-5 pt-5 pb-4">
                      <div className="mb-5 border-t border-white/10" />
                      <p className="text-sm text-white/90">
                        {t("changeLanguage")}
                      </p>
                      <div className="mt-3 max-h-[calc(100vh-250px)] space-y-1 overflow-y-auto pr-1">
                        {LANGUAGES.map((lang) => {
                          const isActive =
                            i18n.language.toLowerCase() ===
                            lang.code.toLowerCase();
                          return (
                            <button
                              key={lang.id}
                              className="flex w-full items-center justify-between rounded-2xl px-3 py-2.5 text-left text-sm text-white/90 transition hover:bg-white/5"
                              onClick={() => {
                                changeLanguageNext(lang.code.toLowerCase());
                                setIsMobileLanguageMenuOpen(false);
                              }}
                            >
                              <span>{lang.name}</span>
                              {isActive && (
                                <CheckIcon className="h-5 w-5 text-emerald-400" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="border-t border-white/10 px-5 py-4">
                      <button
                        className="w-full rounded-full border border-white/10 bg-white/10 py-2.5 text-sm font-semibold text-white transition hover:bg-white/15"
                        onClick={() => setIsMobileLanguageMenuOpen(false)}
                      >
                        {t("close")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </Disclosure>
      {isSupportModalOpen && (
        <SupportModal
          setisSupportModalOpen={setisSupportModalOpen}
          type="landingMobile"
        />
      )}
      {isProductOpen && (
        <div
          onMouseLeave={handleProductClick}
          className={`fixed top-0 bottom-0 overflow-y-auto xl:bottom-auto xl:top-auto xl:absolute z-50  xl:w-11/12 xl:max-w-screen-2xl xl:mx-auto left-0 right-0 xl:overflow-hidden xl:rounded-b-xl`}
        >
          <button
            className="absolute cursor-pointer top-0 right-0 m-4  xl:hidden"
            onClick={handleProductClick}
          >
            <XIcon className="w-6 h-6 text-black" />
          </button>
          <div className="bg-white w-full h-full overflow-auto">
            <div className="p-5 ">
              <h1 className="text-4xl xl:hidden font-extrabold mt-8 mb-8">
                {t("product")}
              </h1>
              <ul className="text-slate-900 xl:grid xl:grid-cols-4">
                <div className="xl:w-[400px]">
                  <li className="flex mb-6 md:mb-4 lg:mb-8 text-sm font-medium">
                    <div className="bg-black shrink-0 basis-auto mr-3 text-white h-5 w-5 flex items-center justify-center rounded-t-full rounded-l-full">
                      <img
                        alt="telegram"
                        src="/img/telegram-product.svg"
                        className="w-3 h-3"
                      />
                    </div>
                    {t("paidSubscriptionCommunitiesTelegram")}
                  </li>

                  <li className="flex mb-6 lg:mb-8 text-sm font-medium">
                    <div className="bg-black shrink-0 basis-auto mr-3 text-white h-5 w-5 flex items-center justify-center rounded-t-full rounded-l-full">
                      {" "}
                      <img
                        alt="telegram"
                        src="/img/telegram-product.svg"
                        className="w-3 h-3"
                      />
                    </div>
                    {t("paidGroupChannelAccessTelegram")}
                  </li>

                  <li className="flex mb-6 lg:mb-8 text-sm font-medium">
                    <div className="bg-black shrink-0 basis-auto mr-3 text-white h-5 w-5 flex items-center justify-center rounded-t-full rounded-l-full">
                      <DownloadIcon className="w-3 h-3" />
                    </div>
                    {t("paidAccessDigitalProducts")}
                  </li>
                  <li className="flex mb-6 lg:mb-8 text-sm font-medium">
                    <div className="bg-black shrink-0 basis-auto mr-3 text-white h-5 w-5 flex items-center justify-center rounded-t-full rounded-l-full">
                      <DownloadIcon className="w-3 h-3" />
                    </div>
                    {t("paidAccessCourses")}
                  </li>
                </div>
                <div className="xl:w-[250px] xl:ml-28">
                  <li className="flex mb-6 text-sm font-medium">
                    <div className="bg-black shrink-0 basis-auto mr-3 text-white h-5 w-5 flex items-center justify-center rounded-t-full rounded-l-full">
                      <TicketIcon className="w-3 h-3" />
                    </div>
                    {t("ticketsPersonEvents")}
                  </li>
                  <li className="flex mb-6 lg:mb-8 text-sm font-medium">
                    <div className="bg-black shrink-0 basis-auto mr-3 text-white h-5 w-[18.29px] flex items-center justify-center rounded-t-full rounded-l-full">
                      <QrcodeIcon className="w-3 h-3" />
                    </div>
                    {t("ticketsScanner")}
                  </li>
                  <li className="flex mb-6 lg:mb-8 text-sm font-medium">
                    <div className="bg-black shrink-0 basis-auto mr-3 text-white h-5 w-[18.29px] flex items-center justify-center rounded-t-full rounded-l-full">
                      <TicketIcon className="w-3 h-3" />
                    </div>
                    {t("ticketsOnlineEvents")}
                  </li>
                  <li className="flex mb-6 lg:mb-8 text-sm font-medium">
                    <div className="bg-black shrink-0 basis-auto mr-3 text-white h-5 w-[18.29px] flex items-center justify-center rounded-t-full rounded-l-full">
                      <ShoppingBagIcon className="w-3 h-3" />
                    </div>
                    {t("paidServices")}
                  </li>
                </div>
                <div className="xl:w-[250px] xl:ml-16">
                  <li className="flex mb-6 lg:mb-8 text-sm font-medium">
                    <div className="bg-black shrink-0 basis-auto mr-3 text-white h-5 w-[18.29px] flex items-center justify-center rounded-t-full rounded-l-full">
                      <VideoCameraIcon className="w-3 h-3" />
                    </div>
                    {t("paidVideoSessions")}
                  </li>
                  <li className="flex mb-6 lg:mb-8 text-sm font-medium">
                    <div className="bg-black shrink-0 basis-auto mr-3 text-white h-5 w-[18.29px] flex items-center justify-center rounded-t-full rounded-l-full">
                      <CalendarIcon className="w-3 h-3" />
                    </div>
                    {t("paidAppointments")}
                  </li>
                  <li className="flex mb-6 lg:mb-8 text-sm font-medium">
                    <div className="bg-black shrink-0 basis-auto mr-3 text-white h-5 w-[18.29px] flex items-center justify-center rounded-t-full rounded-l-full">
                      <ThumbUpIcon className="w-3 h-3" />
                    </div>
                    {t("tipping")}
                  </li>
                  <li className="flex mb-6 lg:mb-8 text-sm font-medium">
                    <div className="bg-black shrink-0 basis-auto mr-3 text-white h-5 w-[18.29px] flex items-center justify-center rounded-t-full rounded-l-full">
                      <CreditCardIcon className="w-3 h-3" />
                    </div>
                    {t("securePayments")}
                  </li>
                </div>
                <div className="xl:w-[280px] xl:ml-8">
                  <li className="flex mb-6 lg:mb-8 text-sm font-medium">
                    <div className="bg-black shrink-0 basis-auto mr-3 text-white h-5 w-[18.29px] flex items-center justify-center rounded-t-full rounded-l-full">
                      <UserCircleIcon className="w-3 h-3" />
                    </div>
                    {t("businessWebsite")}
                  </li>
                  <li className="flex mb-6 lg:mb-8 text-sm font-medium">
                    <div className="bg-black shrink-0 basis-auto mr-3 text-white h-5 w-[18.29px] flex items-center justify-center rounded-t-full rounded-l-full">
                      <CollectionIcon className="w-3 h-3" />
                    </div>
                    {t("portfolioProduct")}
                  </li>
                  <li className="flex mb-6 lg:mb-8 text-sm font-medium">
                    <div className="bg-black shrink-0 basis-auto mr-3 text-white h-5 w-[18.29px] flex items-center justify-center rounded-t-full rounded-l-full">
                      <SparklesIcon className="w-3 h-3" />
                    </div>
                    {t("reviewsProduct")}
                  </li>
                  <li className="flex text-sm font-medium">
                    <div className="bg-black shrink-0 basis-auto mr-3 text-white h-5 w-[18.29px] flex items-center justify-center rounded-t-full rounded-l-full">
                      <UsersIcon className="w-3 h-3" />
                    </div>
                    {t("customersDataManagement")}
                  </li>
                </div>
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
